<template>
  <div class="flex items-center">
    <template v-for="(state, key) in filters.filterStates" :key="key">
      <o-chip
        v-if="!isSearchFilter(state) && !filters.isFilterEmpty(key) && filters.getValueLabel(key)"

        class="text-sm flex align-items  mr-2 flex-shrink-0"
        icon="x"
        icon-after
        variant="info"
        flat
        square
        size="sm"
        @action="filters.clearFilter(key)"
      >
        <div class="flex items-center justify-between flex-wrap">
          <div class="py-2px flex-shrink-0">
            <p>
              <span class="font-semibold pb-2px">{{ state.label }}</span>
            </p>
          </div>
          <div>
            <o-loader v-if="state.isLoading" :scale="0.4" class="flex-shrink-0 h-3 w-3 ml-3" />
            <template v-else>
              <template v-if="filters.getValueLabel(key)">
                <p class="truncate pl-1">
                  - {{ filters.getValueLabel(key) }}
                </p>
              </template>
            </template>
          </div>
        </div>
      </o-chip>
    </template>

    <o-dropdown padding="">
      <o-button flat outline size="sm" icon="filter">
        {{ $t('components.elements.filters.title') }}
      </o-button>

      <template #content>
        <div class="w-full sm:w-80 h-100">
          <o-scroll class="w-full h-100">
            <div class="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-800">
              <p class="text-gray-800 dark:text-gray-100 font-medium">
                {{ $t('components.elements.filters.title') }}
              </p>

              <a class="text-sm text-blue-500 cursor-pointer" @click="filters.clear()">
                {{ $t('general.clearFilters') }}
              </a>
            </div>
            <template v-for="{ key, state } in _filters" :key="key">
              <o-collapse

                :clickable="false"
                header-class="flex flex-row items-center h-10 justify-between px-3 text-gray-700 text-sm font-medium hover:text-gray-800 dark:text-gray-200 dark:hover:text-gray-100"
                body-class="border-t border-gray-200 dark:border-gray-800"
                class="border-b border-gray-200 last:border-none dark:border-gray-800"
              >
                <template #header>
                  <div class="flex justify-between w-full items-center">
                    {{ state.label }}
                    <o-chip
                      v-if="!filters.isFilterEmpty(key) && state.isLoading"
                      size="sm"
                      variant="info"
                      flat
                      square
                      class="mr-3"
                    >
                      <o-loader :scale="0.4" class="h-3 w-3" />
                    </o-chip>
                  </div>
                </template>

                <template #toggle="{ toggle }">
                  <o-button
                    size="sm"
                    transparent
                    icon="accordion"
                    class="-mr-1"
                    @click="toggle"
                  />
                </template>

                <div class="w-full">
                  <component
                    :is="resolveFilterComponent(state.component)"
                    v-model="state.value"
                    v-bind="state.props"
                    :disabled="state.disabled"
                    :options="state.options"
                    :items="state.options"
                    :custom="state.custom"
                    :name="state.label"
                    :status="filters.getValidation(key)?.status ?? null"
                  />
                </div>
              </o-collapse>
            </template>
          </o-scroll>
        </div>
      </template>
    </o-dropdown>
  </div>
</template>

<script lang="ts">
export default { name: 'OFilters' }
</script>

<script setup lang="ts">
import FieldsWithDateRange from '@/components/customer/list/FieldsWithDateRange.vue'
import DateOfBirth from '@/components/customer/list/DateOfBirth.vue'

import {
  OCustomField,
} from '@/components/elements'

import {
  CheckboxField,
  CheckboxGroupField,
  DateRangeOptionalField,
  RadioGroupField,
  SegmentField,
  SelectField,
  TextField,
  ToggleField,
} from '@/components/fields'

import DateRangeFilter from '@/components/filters/elements/DateRange.vue'
import { USelectMenu } from '#components'

import { isSearchFilter } from '@/components/elements/dataGrid/utils'

interface Props {
  filters: any
}

const props = defineProps<Props>()

const filterComponents: Record<string, any> = {
  CheckboxField,
  CheckboxGroupField,
  DateRangeOptionalField,
  DateRangeFilter,
  DateOfBirth,
  FieldsWithDateRange,
  OCustomField,
  RadioGroupField,
  SegmentField,
  SelectField,
  TextField,
  ToggleField,
  USelectMenu,
}

const resolveFilterComponent = (componentName?: string) => {
  if (!componentName) return 'div'
  return filterComponents[componentName] || componentName
}

// Search-typed filters render as a dedicated primary input upstream
// (DataGrid handles this), so we exclude them from both the chip rail and
// the "+ Filter" dropdown. `isSearchFilter` is shared with DataGrid so the
// two stay in sync.
const _filters = computed(() => {
  if (props.filters) {
    if (props.filters.categories) {
      return props.filters.categories
        .reduce((acc: { key: string; state: any }[], category: any) => {
          for (const filterKey of category.filters) {
            const state = props.filters.filterStates[filterKey]
            if (state && !isSearchFilter(state)) acc.push({ key: filterKey, state })
          }
          return acc
        }, [])
    }

    if (props.filters.filters) {
      return (props.filters.filters as any[]).filter((entry: any) => !isSearchFilter(entry.state))
    }

    if (props.filters.categorised) {
      return props.filters.categorised
        .reduce((acc: any[], category: any) => {
          for (const entry of category.filters) {
            if (!isSearchFilter(entry.state)) acc.push(entry)
          }
          return acc
        }, [])
    }
  }

  if (props.filters?.filterStates) {
    return Object.entries(props.filters.filterStates as Record<string, any>)
      .filter(([, state]) => !isSearchFilter(state))
      .map(([key, state]) => ({ key, state }))
  }

  return []
})
</script>
